import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    EventEmitter,
    ViewChild,
    ElementRef, OnChanges, SimpleChanges
} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {setShoppingCartArticles} from '@store/actions/shopping-cart.action';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';
import {FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, Subject} from 'rxjs';
import {ApiArticle} from "@api/articles";
import {ArticleService} from "@iw-shop/article/services/article.service";
import {faInfo, faMinus, faPlus, faQuestionCircle, faShoppingBag, faTrash} from '@fortawesome/free-solid-svg-icons';
import {getSelectedBillingAddress, getSelectedCustomer} from "@store/selectors/user.selector";
import {Popover} from "primeng/popover";
import {ApiConfig} from "@api/configs";
import {DynamicFormGroup} from "@iw-shop/contact/components/contact-dynamic/contact-dynamic.component";

type AtAvailability = ApiConfig['disableOrderForArticles']['atAvailabilities'][number];


@Component({
    selector: 'iw-shop-article-cart-button',
    templateUrl: './article-cart-button.component.html',
    standalone: false,
    styleUrls: ['./article-cart-button.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ArticleCartButtonComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild('op') overlayPanel?: Popover;
    @ViewChild('inputEl', {static: false}) inputEl!: ElementRef;
    @Output() isDisabled = new EventEmitter<boolean>();
    @Output() removeItem = new EventEmitter<string>();

    @Input() article!: ApiArticle;
    @Input() showAddToCartButton: boolean = true;
    @Input() showOverlayPanel: boolean = false;

    private destroy$ = new Subject<void>();
    public value$ = new BehaviorSubject<{ value: number, dispatch: boolean }>({value: 0, dispatch: false});

    // NGRX Signals für synchronen Zugriff (NgRx 16+)
    private configSignal = this.store.selectSignal(state => state.config);
    private customerSignal = this.store.selectSignal(getSelectedCustomer);
    private billingAddressSignal = this.store.selectSignal(getSelectedBillingAddress);

    form = new FormGroup({
        amount: new FormControl(0)
    });
    showArticleInquiryOverlay: boolean = false;
    discountLessThan: number = 0;
    fieldsForInquiry: DynamicFormGroup[] = [];
    // Icons
    readonly icons = {faTrash, faMinus, faPlus, faShoppingBag, faQuestionCircle, faInfo};

    constructor(
        private store: Store<AppState>,
        private articleService: ArticleService
    ) {
    }

    ngOnInit(): void {
        this.initValueSubscription();
        this.setValueFromCart();
        this.updateFieldsForInquiry()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['article']) {
            this.setValueFromCart();
        }
    }

    private initValueSubscription() {
        this.value$.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged((prev, curr) => prev.value === curr.value),
            debounceTime(800)
        ).subscribe(({value, dispatch}) => {
            if (dispatch) {
                this.dispatchUpdate(value);
            }
        });
    }


    private dispatchUpdate(amount: number) {
        this.store.dispatch(setShoppingCartArticles({
            jsonId: 'articleCartButton.update',
            articles: [{amount, articleNumber: this.article.articleNumber}]
        }));

        if (this.showOverlayPanel && amount > 0) {
            this.overlayPanel?.show(null, this.inputEl.nativeElement)
        }
    }

    setValueFromCart() {
        this.store.select(state => state.shoppingCart.items?.articles)
            .pipe(take(1))
            .subscribe(articles => {
                const cartArticle = articles?.find(a => a.articleNumber === this.article.articleNumber);
                const amount = cartArticle?.amount ?? 0;

                this.form.patchValue({amount}, {emitEvent: false});
                this.value$.next({value: amount, dispatch: false});
            });
    }

    /**
     * Ermittelt die aktuelle Verfügbarkeitskonfiguration basierend auf dem Artikelstatus
     */
    get currentAvailabilityConfig(): AtAvailability | null {
        const config = this.configSignal();
        const customer = this.customerSignal();

        if (!config) return null;

        const priceGroup = customer?.priceGroup || config.defaultPriceGroup || '';
        const list = config.disableOrderForArticles?.atAvailabilities || [];

        const currentStatus = this.articleService.getAvailability(
            this.article.inventory || 0,
            this.article.availability
        );

        return list.find(item =>
            item.availability === currentStatus &&
            ((item.requiredPriceGroups?.length ?? 0) === 0 || item.requiredPriceGroups?.includes(priceGroup))
        ) || null;
    }

    /**
     * Prüft ob der Button deaktiviert sein muss und emittet den Status
     */
    get isOrderDisabled(): boolean {
        const isDisabled = !!this.currentAvailabilityConfig;
        // Emit nur bei Änderung, um Infinite Loops zu vermeiden
        this.isDisabled.emit(isDisabled);
        return isDisabled;
    }

    /**
     * Helfer für das Template, um Texte anzuzeigen (Anfrage-Text etc.)
     */
    get availabilityInfo() {
        return this.currentAvailabilityConfig;
    }

    increase() {
        this.setValue((this.form.value.amount ?? 0) + 1);
    }

    decrease() {
        const current = this.form.value.amount ?? 0;
        if (current > 0) this.setValue(current - 1);
    }

    change(e: Event) {
        const input = e.target as HTMLInputElement;
        const value = parseInt(input.value, 10) || 0;
        this.setValue(Math.max(0, value));
    }

    setValue(value: number, dispatch: boolean = true) {
        this.form.patchValue({amount: value});

        if (value === 0 && dispatch) {
            this.removeItem.emit(this.article.articleNumber);
        }

        this.value$.next({value, dispatch});
    }

    getCustomerHtml(): string {
        const address = this.billingAddressSignal();
        const customer = this.customerSignal();

        if (!address || !customer) return '';

        return `${address.gesoftName1} ${address.gesoftName2}<br>` +
            `Kd-Nr.: ${customer.customerNumber}<br>` +
            `E-Mail / Stammdaten: ${customer.email}<br>` +
            `Telefon: ${address.phone}<br>`;
    }

    updateFieldsForInquiry(): void {
        this.fieldsForInquiry = [];
        this.fieldsForInquiry.push({
            headline: 'Anfrage zu ' + this.article.name,
            field: {
                label: 'Artikel',
                value: this.article.articleNumber + ' ' + this.article.name,
                disabled: true,
                hidden: false
            }
        });

        const customerHTML = this.getCustomerHtml();
        if (customerHTML.length > 0) {
            const customer = this.customerSignal();
            this.fieldsForInquiry.push({field: {label: 'Kunde', value: customerHTML, hidden: true}});
            this.fieldsForInquiry.push({
                field: {
                    label: 'E-Mail',
                    value: customer?.email,
                }
            });
        } else {
            this.fieldsForInquiry.push({
                field: {
                    label: 'E-Mail',
                    value: '',
                    required: true,
                    input: {regex: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'}
                }
            });
            this.fieldsForInquiry.push({field: {label: 'Telefon', value: '', required: true}});
            this.fieldsForInquiry.push({field: {label: 'Name', value: '', required: true}});
            this.fieldsForInquiry.push({field: {label: 'Straße', value: '', required: true}});
            this.fieldsForInquiry.push({
                fieldsInRow: [
                    {label: 'Ort', value: '', required: true},
                    {label: 'Stadt', value: '', required: true}

                ]
            });
        }
        this.fieldsForInquiry.push({field: {label: 'Nachricht:', textarea: {}}});

    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
